import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Trash2, GripVertical, RefreshCw } from "lucide-react";
import { Input } from "../../components/ui/input";
import { adminApi, apiError } from "../api";
import { resolveImg } from "../../context/SiteContext";
import { ImagePickerDialog } from "../components/ImagePicker";

export default function GalleryManager() {
  const [items, setItems] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [replaceFor, setReplaceFor] = useState(null);
  const dragIndex = useRef(null);
  const inputRef = useRef(null);

  const load = () => adminApi.get("/admin/gallery").then(({ data }) => setItems(data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const bulkUpload = async (fileList) => {
    const arr = Array.from(fileList);
    const uploaded = [];
    for (const file of arr) {
      setUploads((u) => [...u, { key: file.name, pct: 0 }]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("folder", "gallery");
        const { data } = await adminApi.post("/media/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            const pct = Math.round((e.loaded / (e.total || 1)) * 100);
            setUploads((u) => u.map((x) => (x.key === file.name ? { ...x, pct } : x)));
          },
        });
        uploaded.push({ url: data.webp_url || data.url, caption: "" });
      } catch (e) {
        toast.error(apiError(e));
      } finally {
        setUploads((u) => u.filter((x) => x.key !== file.name));
      }
    }
    if (uploaded.length) {
      await adminApi.post("/admin/gallery", uploaded);
      toast.success(`${uploaded.length} image(s) added to the Gallery page`);
      load();
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this image from the gallery?")) return;
    try {
      await adminApi.delete(`/admin/gallery/${id}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveCaption = async (id, caption) => {
    try {
      await adminApi.put(`/admin/gallery/${id}`, { caption });
      toast.success("Caption saved");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const onDrop = async (targetIdx) => {
    const from = dragIndex.current;
    if (from === null || from === targetIdx) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(targetIdx, 0, moved);
    setItems(next);
    dragIndex.current = null;
    try {
      await adminApi.put("/admin/gallery/reorder", next.map((x) => x.id));
      toast.success("Order saved");
    } catch (e) {
      toast.error(apiError(e));
      load();
    }
  };

  return (
    <div data-testid="admin-gallery-page">
      <h1 className="text-2xl font-bold mb-1">Gallery Manager</h1>
      <p className="text-sm text-muted-foreground mb-6">Bulk upload, drag to reorder, add captions — the public Gallery page updates automatically.</p>

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.length) bulkUpload(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className="border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer bg-white dark:bg-slate-900 hover:border-emerald-400 transition-colors mb-5"
        data-testid="gallery-dropzone"
      >
        <UploadCloud className="h-8 w-8 mx-auto text-emerald-500 mb-2" />
        <div className="text-sm font-medium">Drop multiple images here or click to bulk upload</div>
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { bulkUpload(e.target.files); e.target.value = ""; }} data-testid="gallery-file-input" />

      {uploads.length > 0 && (
        <div className="mb-4 space-y-2">
          {uploads.map((u) => (
            <div key={u.key} className="flex items-center gap-3 text-sm">
              <span className="truncate flex-1">{u.key}</span>
              <div className="h-2 w-48 rounded-full bg-muted overflow-hidden"><div className="h-full bg-emerald-500 transition-all" style={{ width: `${u.pct}%` }} /></div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((g, i) => (
          <div
            key={g.id}
            draggable
            onDragStart={() => (dragIndex.current = i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(i); }}
            className="rounded-xl border bg-white dark:bg-slate-900 overflow-hidden cursor-grab active:cursor-grabbing"
            data-testid={`gallery-item-${i}`}
          >
            <div className="relative aspect-video bg-muted overflow-hidden group">
              <img src={resolveImg(g.url)} alt={g.caption || ""} className="h-full w-full object-cover" loading="lazy" />
              <div className="absolute top-1.5 left-1.5 h-6 w-6 rounded bg-black/50 text-white grid place-items-center"><GripVertical className="h-3.5 w-3.5" /></div>
              <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => setReplaceFor(g.id)} className="h-6 w-6 rounded bg-black/60 text-white grid place-items-center" title="Replace image" data-testid={`gallery-replace-${i}`}><RefreshCw className="h-3 w-3" /></button>
                <button onClick={() => remove(g.id)} className="h-6 w-6 rounded bg-red-500 text-white grid place-items-center" title="Delete" data-testid={`gallery-delete-${i}`}><Trash2 className="h-3 w-3" /></button>
              </div>
            </div>
            <div className="p-2">
              <Input
                defaultValue={g.caption}
                placeholder="Add caption…"
                className="h-8 text-xs"
                onBlur={(e) => e.target.value !== g.caption && saveCaption(g.id, e.target.value)}
                data-testid={`gallery-caption-${i}`}
              />
            </div>
          </div>
        ))}
      </div>

      <ImagePickerDialog
        open={!!replaceFor}
        onOpenChange={(v) => !v && setReplaceFor(null)}
        defaultFolder="gallery"
        onSelect={async (url) => {
          try {
            await adminApi.put(`/admin/gallery/${replaceFor}`, { url });
            toast.success("Image replaced");
            setReplaceFor(null);
            load();
          } catch (e) {
            toast.error(apiError(e));
          }
        }}
      />
    </div>
  );
}
